// src/hooks/useBilling.js
import { useEffect, useState, useCallback } from "react";

// Subscription statuses that are considered "active" for feature access.
const ACTIVE_STATUSES = ["active", "trialing", "past_due"];

// Flatten [{key, valueInt/valueString/valueJson}] → { [key]: value }
// Used for paid plan features stored in PlanFeature rows.
function flattenFeatures(list = []) {
  const out = {};
  for (const f of list) {
    out[f.key] = f.valueInt ?? f.valueString ?? f.valueJson ?? true;
  }
  return out;
}

// Convert a Free Trial's String[] feature list → { [key]: true }
// e.g. ["feat.branding", "feat.3d"] → { "feat.branding": true, "feat.3d": true }
function trialFeaturesToMap(list = []) {
  const out = {};
  for (const key of list) {
    if (typeof key === "string" && key) out[key] = true;
  }
  return out;
}

export default function useBilling() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // 1) Who am I?
      const authRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
        { credentials: "include" }
      );
      if (!authRes.ok) throw new Error("auth");
      const auth = await authRes.json();

      const isOrg = !!auth?.primaryOrg;
      const endpoint = isOrg
        ? `${process.env.REACT_APP_BACKEND_URL}/api/org-billing/me`
        : `${process.env.REACT_APP_BACKEND_URL}/api/billing/me`;

      // 2) Scope-specific billing
      const r = await fetch(endpoint, { credentials: "include" });
      if (!r.ok) throw new Error("billing");
      const raw = await r.json();

      // 3) Resolve access state.
      //
      //    For org accounts, the backend has two independent access paths:
      //
      //    a) Free Trial  — stored on the Org record (trialFeatures: String[])
      //       raw.trial.isActive === true  →  features from raw.trial.features
      //
      //    b) Paid plan   — stored in OrgSubscription + Plan + PlanFeature
      //       raw.hasActiveSubscription === true  →  features from raw.subscription.plan.features
      //
      //    We must check the Free Trial path first because hasActiveSubscription
      //    is always false during a trial (no OrgSubscription row exists yet).

      const trial = raw?.trial ?? null;
      const isActiveTrial = isOrg && trial?.isActive === true;

      const sub = raw?.subscription ?? null;
      const subStatus = sub?.status ?? null;
      const hasPaidActive =
        !!raw?.hasActiveSubscription || ACTIVE_STATUSES.includes(subStatus);

      const hasActive = isActiveTrial || hasPaidActive;

      let planCode = "none";
      let planName = "No Active Plan";
      let features = {};

      if (isActiveTrial) {
        // Free Trial is running — features are a plain String[] on the Org record
        planCode = "free-trial";
        planName = "Free Trial";
        features = trialFeaturesToMap(trial.features);
      } else if (sub?.plan && hasPaidActive) {
        // Paid (or Stripe-trialing) subscription
        planCode = sub.plan.code || "unknown";
        planName = sub.plan.name || "Plan";

        if (isOrg) {
          features = flattenFeatures(sub.plan.features || []);
        } else {
          // Individual: prefer server-computed features if present
          features =
            raw?.features && Object.keys(raw.features).length
              ? raw.features
              : flattenFeatures(sub.plan.features || []);
        }
      }
      // else: expired trial, canceled/no subscription → features stay {}

      setMe({
        scope: isOrg ? "organization" : "individual",
        hasActiveSubscription: hasActive,
        subscription: sub,
        trial: isOrg ? trial : null,
        plan: { code: planCode, name: planName },
        features,
        org: raw?.org ?? null,
        user: raw?.user ?? null,
      });
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { me, loading, reload };
}
